"use client";

import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Input,
  Label,
  Textarea,
} from '@pagespeedify/ui';
import { Loader2, Mail } from 'lucide-react';
import { useApi } from '../lib/apiClient';
import { toast } from 'sonner';

interface ContactFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ContactFormModal: React.FC<ContactFormModalProps> = ({ isOpen, onClose }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const api = useApi();

  const { mutate: sendContactForm, isPending } = useMutation({
    mutationFn: async (data: { subject: string; message: string }) => {
      return api.post('/contact', data);
    },
    onSuccess: () => {
      toast.success('Your message has been sent!');
      onClose();
    },
    onError: (error: any) => {
      const errorData = error.response?.data;
      let title = 'Failed to send message';
      let description = errorData?.message || error.message || 'Please try again.';

      switch (errorData?.error) {
        case 'unauthorized':
          title = 'Unauthorized';
          description = 'You must be logged in to send a message.';
          break;
        case 'configuration_error':
          title = 'Server Configuration Error';
          description = 'The server is not configured to send emails. Please contact support.';
          break;
        case 'not_found':
          title = 'User Not Found';
          description = 'Your user account could not be found. Please try logging in again.';
          break;
        case 'server_error':
          title = 'Server Error';
          description = 'A server error occurred. Please try again later.';
          break;
      }

      toast.error(title, {
        description,
      });
    },
  });

  const handleSubmit = () => {
    if (!subject || !message) {
      toast.error('Please fill out both subject and message fields.');
      return;
    }
    sendContactForm({ subject, message });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Mail className="mr-2" />
            Contact Support
          </DialogTitle>
          <DialogDescription>
            Have a question or need help? Send us a message and we'll get back to you as soon as possible.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              placeholder="e.g., Question about my subscription"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={isPending}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              placeholder="Your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isPending}
              rows={6}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {isPending ? 'Sending...' : 'Send Message'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};